import FormLayout from "components/form/FormLayout";
import Input from "components/form/input";
import Sheet from "components/sheet/Sheet";
import Tabs from "components/tabs/Tabs";
import Portal from "elements/Portal";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { IReward, ITier } from "redux/reducers/models/loyalty";
import { Select } from "components/select/index";
import axios, { CancelToken } from "axios";
import { GetProductTypes } from "api/rpc/2024-04/facilityAdmin/product/type";
import { StatusCode } from "api/protocols";
import { showError } from "redux/actions/ui";
import { IProduct, TProductType } from "redux/reducers/models/product";
import { GetProduct } from "api/rpc/2024-04/facilityAdmin/product/product";
import useCancelToken from "hooks/useCancelToken/useCancelToken";
import { ButtonNew as Button } from "components/buttonNew";
import Divider from "components/divider";
import useCurrencyFacility from "hooks/useCurrency/useCurrencyFacility";
import "../loyalty.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationCircle, faXmark } from "@fortawesome/pro-regular-svg-icons";

interface IProps {
    open: boolean;
    onCancel: () => void;
    onOk: (tier: Partial<ITier> & {rewards: Array<Partial<IReward & {product_type_ids?: Array<number>; product_ids?: Array<number>}>>}) => void;
    tier?: ITier & {rewards?: Array<Partial<IReward & {products?: Array<IProduct>; product_types?: Array<TProductType>}>>};
}

interface IState {
    id?: number;
    title: string;
    points_required?: string;
}

enum TierTab {
    General = 0,
    Rewards = 1,
}

interface ITempReward {
    id?: number;
    name: string;
    reward_type: "discount_percent" | "discount_amount" | "free_product";
    application: "all_products" | "product_categories" | "product" | "order";
    discount_value?: string;
    points_required?: string;
    selectedTypes?: Array<TProductType & {name?: string}>;
    typeSearch?: string;
    selectedProducts: Array<IProduct & {name?: string}>;
    productSearch?: string;
}

export default function LoyaltyProgramTiersModal(props: IProps) {
    const {open, onCancel, onOk, tier} = props;
    const {t} = useTranslation();
    const dispatch = useAppDispatch();
    const { getCurrencySymbol } = useCurrencyFacility();
    const {facilityStore} = useAppSelector(store => store);
    const { cancelToken, cancelRequest } = useCancelToken();
    const [types, setTypes] = useState<Array<TProductType>>([]);
    const { Option } = Select;
    const [state, setState] = useState<IState>({
        id: null,
        title: "",
        points_required: "",
    });
    const [rewards, setRewards] = useState<Array<Partial<ITempReward>>>([]);
    const [products, setProducts] = useState<Array<IProduct & {name?: string}>>([]);

    const [selectedTab, setSelectedTab] = useState<TierTab>(TierTab.General);
    const productSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        const source = axios.CancelToken.source();
        void getProductTypes(source.token);
        return () => source.cancel();
    }, []);


    useEffect(() => {
        if (!open) {
            ReactDOM.unstable_batchedUpdates(() => {
                setState({
                    id: null,
                    title: "",
                    points_required: "",
                });
                setSelectedTab(TierTab.General);
            });
        } else {
            if (tier) {
                ReactDOM.unstable_batchedUpdates(() => {
                    setState({
                        ...tier,
                        points_required: tier?.points_required?.toString(),
                    });
                    if (tier?.rewards?.length > 0) {
                        setRewards(tier?.rewards?.map(reward => ({...reward, 
                            selectedProducts: [...reward.products]?.map(product => ({...product, name: product?.preferred_title ? product?.preferred_title : product?.title})),
                            selectedTypes: [...reward.product_types]?.map(type => ({...type, name: type?.title})),
                            productSearch: "",
                            typeSearch: "",
                            points_required: reward?.points_required?.toString(),
                            discount_value: reward?.discount_value?.toString(),
                        })));
                    } else {
                        setRewards([{
                            name: "",
                            points_required: "",
                            reward_type: "discount_percent",
                            application: "product",
                            discount_value: "",
                            selectedTypes: [],
                            typeSearch: "",
                            selectedProducts: [],
                            productSearch: "",
                        }]);
                    }
                    
                });
                
            } else {
                setRewards([{
                    name: "",
                    points_required: "",
                    reward_type: "discount_percent",
                    application: "product",
                    discount_value: "",
                    selectedTypes: [],
                    typeSearch: "",
                    selectedProducts: [],
                    productSearch: "",
                }]);
            }
        }
    }, [open]);

    const tabs = [
        {
          id: "general-1",
          content: t("General"),
        },
    
        {
          id: "rewards-1",
          content: t("Rewards"),
        },
      ];

      async function getProductTypes(cancelToken?: CancelToken) {
        const res = await GetProductTypes(null, false, cancelToken);
        if (res?.status !== StatusCode.OK) {
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            dispatch(showError(t("Error getting product types")));
            return;
        }
        setTypes(res?.data);
    }

    async function getProducts(search: string, cancelToken?: CancelToken) {
        //loader
        if (products !== undefined) {
            setProducts(undefined);
        }
        const res = await GetProduct({limit: 50, search: search}, false, cancelToken);
        if (res?.status !== StatusCode.OK) {
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            setProducts([]);
            dispatch(showError(t("Error getting products")));
            return;
        }
        setProducts(res?.data);
    }

    function handleProductSearch(search: string, index: number) {
        void cancelRequest();
        const updatedRewards = [...rewards]?.map((reward, i) => ({...reward, productSearch: i === index ? search : ""}));
        setRewards(updatedRewards);
        if (productSearchTimeoutRef.current) {
            clearTimeout(productSearchTimeoutRef.current);
          }
          productSearchTimeoutRef.current = setTimeout(() => {
            void getProducts(search, cancelToken());
          }, 500);
    }

      function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
        const {id, value } = e.target;

        setState(prev => ({...prev, [id]: value}));
    }

    function handleRewardInputChange(e: React.ChangeEvent<HTMLInputElement>, index: number) {
        const {id, value } = e.target;
        const updatedRewards = [...rewards];
        updatedRewards[index] = {...updatedRewards[index], [id]: value};
        setRewards(updatedRewards);
    }

    function handleRewardDropdownChange(id: keyof ITempReward, index: number, value: string) {
        const updatedRewards = [...rewards];
        updatedRewards[index] = {...updatedRewards[index], [id]: value};
        setRewards(updatedRewards);
    }

    function handleOk() {
        if (!state?.title) {
            dispatch(showError(t("Title required")));
            return;
        } else if (state?.points_required === "") {
            dispatch(showError(t("Points amount required")));
            return;
        } else if (rewards?.some(reward => !reward?.name || reward?.points_required === "" || reward?.discount_value === "")) {
            dispatch(showError(t("Please fill title, points amount and discount field for every reward")));
            return;
        } else if (rewards?.some(reward => reward?.reward_type === "discount_percent" && Math.abs(Number(reward?.discount_value)) > 100 )) {
            dispatch(showError(t("Discount percent cannot be over 100")));
            return;
        }

        void onOk({
            ...state,
            points_required: Math.round(Math.abs(Number(state?.points_required))),
            rewards: rewards?.map(reward => ({...reward, 
                discount_value: Math.abs(Number(reward?.discount_value)), 
                points_required: Math.round(Math.abs(Number(reward?.points_required))),
                product_ids: reward?.application === "product" ? reward?.selectedProducts?.map(product => product?.id) : undefined,
                product_type_ids: reward?.application === "product_categories" ? reward?.selectedTypes?.map(type => type?.id) : undefined,
            })),
        });
    }

    return (
        <Portal isMounted={open}>
        <Sheet
            open={open}
            size="small"
            title={t("Tier")}
            supportText={t("Manage tiers and rewards to motivate your customers")}
            closable
            onCancel={onCancel}
            onOk={handleOk}
            okText={t("Save")}
            >
                <Tabs tabs={tabs} selected={selectedTab} onSelect={index => setSelectedTab(index)}>
                    <br />
                {selectedTab === TierTab.General ? (
                    <FormLayout>
                        <FormLayout.Group>
                            <Input label={t("Tier Name")} maxLength={100} placeholder={t("Tier Name")} value={state?.title} onChange={handleInputChange} id="title" />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input label={t("Points Amount")} type="number" placeholder={100} value={state?.points_required} onChange={handleInputChange} id="points_required" suffix={t("points")} helpText={t("Number of points required to enter tier")} />
                        </FormLayout.Group>
                    </FormLayout>
                ) : (
                    <FormLayout>
                    {rewards?.map((reward, index) => {
                        return (
                            <React.Fragment key={index}>
                                <FormLayout.Group>
                                    <Input label={t("Reward Name")} maxLength={100} placeholder={t("Reward Name")} value={reward?.name} onChange={e => handleRewardInputChange(e, index)} id="name" />
                                </FormLayout.Group>

                                <FormLayout.Group>
                                <Select
                                    onChange={val => handleRewardDropdownChange("application", index, val as string)}
                                    placeholder={t("Select Application")}
                                    allowClear
                                    showDropDownOnFocus={true}
                                    usePortal
                                    defaultValue={reward?.application}
                                    label={t("Apply To")}
                                >
                                    {[{id: "product_categories", label: `${t("Specific Product Category")}`}, {id: "product", label: `${t("Specific Product")}`}]?.map((option, index) => {
                                    return (
                                        <Option key={index} value={option?.id} extraValues={option}>
                                        {option?.label}
                                        </Option>
                                    );
                                    })}
                                </Select>
                             </FormLayout.Group>
                             {reward?.application === "product_categories" ? (
                                 <>
                                <FormLayout.Group>
                                     <Select
                                        showSearch
                                        onSearch={(query: string) => setRewards(prev => prev?.map((prevReward, i) => i === index ? {...prevReward, typeSearch: query} : prevReward))}
                                        onChange={(_, extraValue: TProductType & { name?: string;}) => setRewards(prev => prev?.map((prevReward, i) => i === index ? {...prevReward, selectedTypes: [...prevReward.selectedTypes, extraValue]} : prevReward))}
                                        allowClear
                                        searchValue={reward?.typeSearch}
                                        searching={!types}
                                        showDropDownOnFocus
                                        usePortal
                                        placeholder={t("Search product categories")}
                                        noData={
                                            <div className="flex items-center p-2 text-black">
                                            <FontAwesomeIcon icon={faExclamationCircle} fixedWidth size="1x" />
                                            <span className="ml-3 italic">{t("No product category found...")}</span>
                                            </div>
                                        }
                                        >
                                        {types?.filter(type => !reward?.selectedTypes?.some(selectedType => selectedType?.id === type?.id) && type?.title?.toLocaleLowerCase()?.includes(reward?.typeSearch?.toLocaleLowerCase()))?.map((type, index) => {
                                            return (
                                            <Option key={index} value={type?.id} extraValues={type}>
                                            <p>{type?.title}</p>
                                            </Option>
                                            );
                                        })}
                                        </Select>
                                </FormLayout.Group>
                                <FormLayout.Group>
                                        {/* Selected product category tags */}
                                        <div className="loyalty-tags-container">
                                            {reward?.selectedTypes?.map((type, typeIndex) => {
                                                return (
                                                    <div key={typeIndex} className="loyalty-tag">
                                                        <p className="loyalty-tag-text">{type?.title}</p>
                                                        <FontAwesomeIcon icon={faXmark} className="loyalty-tag-x" onClick={() => setRewards(prev => prev?.map((prevReward, i) => i === index ? {...prevReward, selectedTypes: prevReward?.selectedTypes?.filter(prevType => prevType?.id !== type?.id)} : prevReward))} />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        </FormLayout.Group>
                                </>
                                ) : reward?.application === "product" ? <><FormLayout.Group>
                                    <Select
                                        showSearch
                                        onSearch={(query: string) => handleProductSearch(query, index)}
                                        onChange={(_, extraValue: IProduct & {
                                            name?: string;
                                        }) => setRewards(prev => prev?.map((prevReward, i) => i === index ? {...prevReward, selectedProducts: [...prevReward.selectedProducts, extraValue]} : prevReward))}
                                        allowClear
                                        searchValue={reward?.productSearch}
                                        searching={!products}
                                        showDropDownOnFocus
                                        usePortal
                                        placeholder={t("Search products")}
                                        noData={
                                            <div className="flex items-center p-2 text-black">
                                            <FontAwesomeIcon icon={faExclamationCircle} fixedWidth size="1x" />
                                            <span className="ml-3 italic">{t("No product found...")}</span>
                                            </div>
                                        }
                                        >
                                        {products?.filter(product => !reward?.selectedProducts?.some(selectedProduct => selectedProduct?.id === product?.id))?.map((product, index) => {
                                            return (
                                            <Option key={index} value={product?.id} extraValues={product}>
                                            <p>{product?.preferred_title ? product?.preferred_title : product?.title}</p>
                                            {product?.subtitle ? <p className="loyalty-product-subtitle">{product?.subtitle}</p> : null}
                                            </Option>
                                            );
                                        })}
                                        </Select>
                        </FormLayout.Group>
                        <FormLayout.Group>
                        {/* Selected product tags */}
                        <div className="loyalty-tags-container">
                            {reward?.selectedProducts?.map((product, productIndex) => {
                                return (
                                    <div key={productIndex} className="loyalty-tag">
                                        <p className="loyalty-tag-text">{product?.preferred_title ? product?.preferred_title : product?.title} {product?.subtitle ? ` • ${product?.subtitle}` : null}</p>
                                        <FontAwesomeIcon icon={faXmark} className="loyalty-tag-x" onClick={() => setRewards(prev => prev?.map((prevReward, i) => i === index ? {...prevReward, selectedProducts: prevReward?.selectedProducts?.filter(prevProduct => prevProduct?.id !== product?.id)} : prevReward))} />
                                    </div>
                                );
                            })}
                        </div>
                        </FormLayout.Group>
                        </> : null}
                             <FormLayout.Group>
                             <Input 
                                suffix={t("points")}
                                type="number" 
                                label={t("Points Amount")} 
                                onChange={e => handleRewardInputChange(e, index)} 
                                value={reward?.points_required} 
                                id="points_required" 
                                placeholder={"100"} />
                                <Input 
                                prefix={reward?.reward_type === "discount_percent" || reward?.reward_type === "free_product" ? "%" : getCurrencySymbol(facilityStore?.facility?.default_currency_code)}
                                type="number" 
                                label={reward?.reward_type === "discount_percent" || reward?.reward_type === "free_product" ? t("Discount (%)") : `${t("Discount")} (${getCurrencySymbol(facilityStore?.facility?.default_currency_code)})`} 
                                onChange={e => handleRewardInputChange(e, index)} 
                                value={reward?.discount_value} 
                                id="discount_value" 
                                placeholder={"10"} />
                             </FormLayout.Group>
                             {rewards?.length > 1 && index !== rewards?.length - 1 ? (
                                 <FormLayout.Group>
                                 <Divider/>
                                 </FormLayout.Group>
                             ) : null}
                             {index === rewards?.length - 1 ? (
                                 <FormLayout.Group>
                                 <Button type="text" size="medium" onClick={() => setRewards(prev => [...prev, {name: "",
                                 points_required: "",
                                 reward_type: "free_product",
                                 application: "product",
                                 discount_value: "",
                                 selectedTypes: [],
                                 typeSearch: "",
                                 selectedProducts: [],
                                 productSearch: ""}])}>Add Reward +</Button>
                             </FormLayout.Group>
                             ) : null}
                             </React.Fragment>
                        );
                    })}
                    </FormLayout>
                   
                )}
                </Tabs>
            </Sheet>
        </Portal>
    );

}